// ---------------------------------------------------------------------------
// Shadow DOM container – creates / destroys the isolated widget root
// ---------------------------------------------------------------------------

import { styles } from "./styles";

const CONTAINER_ID = "patch-pilot-root";

let hostDiv: HTMLDivElement | null = null;
let shadow: ShadowRoot | null = null;

/**
 * Creates a container div, attaches Shadow DOM, injects styles,
 * and appends it to the given host (defaults to document.body).
 * Returns the ShadowRoot for other UI modules to render into.
 */
export function createContainer(
  hostElement?: HTMLElement,
): ShadowRoot {
  // Prevent duplicate containers
  if (shadow) return shadow;

  const host = hostElement ?? document.body;

  hostDiv = document.createElement("div");
  hostDiv.id = CONTAINER_ID;
  // Prevent inheriting page styles on the host element itself
  hostDiv.style.all = "initial";
  hostDiv.style.position = "fixed";
  hostDiv.style.zIndex = "9999";
  hostDiv.style.top = "0";
  hostDiv.style.left = "0";
  hostDiv.style.width = "0";
  hostDiv.style.height = "0";
  hostDiv.style.overflow = "visible";

  shadow = hostDiv.attachShadow({ mode: "open" });

  const styleEl = document.createElement("style");
  styleEl.textContent = styles;
  shadow.appendChild(styleEl);

  host.appendChild(hostDiv);

  return shadow;
}

/**
 * Sets the `data-theme` attribute on the shadow host for CSS variable theming.
 */
export function setContainerTheme(theme: "light" | "dark" | "auto"): void {
  if (!hostDiv) return;
  hostDiv.setAttribute("data-theme", theme);
}

/**
 * Removes the container from the DOM and clears references.
 */
export function destroyContainer(): void {
  if (hostDiv) {
    hostDiv.remove();
    hostDiv = null;
    shadow = null;
  }
}
